import type { MutableRefObject, ReactNode } from 'react';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useOnMainEditorResize from '../post/useOnMainEditorResize';
import './WiderLineSizeContext.module.scss';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const widerLineTotalSizeProperty = '--wider-line-total-width';
const MAX_WIDER_LINE_WIDTH = 1400;

const MINIMUM_SPACING = {
  // Space for gutter buttons (ex reorder button)
  LEFT: 32,
  // Space for comment avatar
  RIGHT: 52,
};

const getPaddings = (node: Element) => {
  const style = getComputedStyle(node);
  const left = parseInt(style.paddingLeft, 10);
  const right = parseInt(style.paddingRight, 10);
  return { left, right };
};

/**
 * Get widths for right overflow and total.
 *
 *  Left overflow            Right overflow
 *       +                        +
 *       |                        |
 * +--------------Container-------------+
 * |     |  +------Editor------+  |     |
 * |     |  |                  |  |     |
 * |  +------------------------------+  |
 * |  |  |  |                  |  |  |  |
 * |  +--v--+                  +--v--+  |
 * |  |     +------------------+     |  |
 * +------------------------------------+
 *    |                              |
 *    +----^-------------------------+
 *    |    |                         |
 *    |    |     Wider line          |
 *    +-----------ex-image-----------+
 *         |
 *         +
 *       Total
 */
const getOverflowWidths = (container: Element, editor: Element) => {
  const containerRect = container.getBoundingClientRect();
  const editorRect = editor.getBoundingClientRect();
  const editorLeftOffset = editorRect.left - containerRect.left;

  const paddings = getPaddings(editor);
  const rightPaddingOffset = paddings.right - paddings.left;

  const maxRightOverflow =
    containerRect.width -
    editorLeftOffset -
    editorRect.width -
    MINIMUM_SPACING.RIGHT;
  const maxWidthWithRightConstraint =
    maxRightOverflow * 2 + rightPaddingOffset + editorRect.width;

  const maxLeftOverflow = editorLeftOffset - MINIMUM_SPACING.LEFT;
  const maxWidthWithLeftConstraint =
    maxLeftOverflow * 2 - rightPaddingOffset + editorRect.width;

  const total = Math.min(
    MAX_WIDER_LINE_WIDTH,
    maxWidthWithRightConstraint,
    maxWidthWithLeftConstraint,
  );

  const editorContentWidth = editorRect.width - paddings.left - paddings.right;
  const rightOverflow = (total - editorRect.width - rightPaddingOffset) / 2;
  if (total < editorContentWidth || rightOverflow < 0) {
    return {
      total: editorContentWidth,
      rightOverflow: 0,
    };
  }

  return { total, rightOverflow };
};

const Context = createContext({ rightOverflowSize: 0 });

interface WiderLineSizeContextProps {
  containerRef: MutableRefObject<HTMLElement | null>;
  editor?: HTMLElement | null;
  children: ReactNode;
}

const WiderLineSizeContext = ({
  containerRef,
  editor,
  children,
}: WiderLineSizeContextProps) => {
  const [rightOverflowSize, setRightOverflowSize] = useState(0);

  const handleResize = useCallback(() => {
    if (editor && containerRef.current) {
      const { total, rightOverflow } = getOverflowWidths(
        containerRef.current,
        editor,
      );
      editor.style.setProperty(widerLineTotalSizeProperty, `${total}px`);
      setRightOverflowSize(rightOverflow);
    }
  }, [containerRef, editor]);

  useEffect(() => {
    handleResize();
    const containerEl = containerRef.current;
    if (!containerEl) return undefined;
    containerEl.addEventListener('transitionend', handleResize);

    return () => {
      containerEl.removeEventListener('transitionend', handleResize);
    };
  }, [handleResize, containerRef]);

  useOnMainEditorResize(handleResize);

  const value = useMemo(() => ({ rightOverflowSize }), [rightOverflowSize]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default WiderLineSizeContext;
